/**
 * Which providers write a title's name and table type, hourly, from their own catalogue.
 *
 * SA's host import and JILI's game-list import both upsert the name and the table type of every
 * title they list, so an operator's edit to either would be reverted within the hour with no trace
 * of what undid it. For those drivers the two boxes are read-only. A driver that writes neither
 * (the mock one) leaves them editable. Separate from the dialog so the decision is testable.
 */
const HOURLY_CATALOGUE_DRIVERS: ReadonlySet<string> = new Set(['SA', 'JILI']);

export function providerOwnsTitle(driver: string | null | undefined): boolean {
  return driver !== null && driver !== undefined && HOURLY_CATALOGUE_DRIVERS.has(driver);
}

/** What that hourly job is called in a sentence: SA's pulls "host" tables, JILI's a game list. */
export function hourlyImportName(driver: string | null | undefined): string {
  return driver === 'JILI' ? 'hourly game-list import' : 'hourly host import';
}

/**
 * Why a title's bet range, jackpot contribution and demo mode read "Not stored", in the terms of the
 * title's own provider. A JILI title used to be told SA's reason (module walk, 30 Oct 2026).
 *
 * Each reason names what OUR launch sends that provider. JILI's is LoginWithoutRedirect with the
 * token, game, language, return address and device (services/api games/driver/jili.ts). A title of
 * any other provider is told what is true of every launch here, since only SA and JILI launch at all.
 */
export function notStoredReason(driver: string | null | undefined): string {
  const lead = 'Bet range, jackpot contribution and demo mode read “Not stored” because nothing on the launch path could act on them: ';
  const end = ' A figure typed here would reach no player — this is a capability the platform does not have, not a field waiting to be added.';
  if (driver === 'SA')
    return `${lead}SA’s launch request carries no demo-session parameter, and its error catalogue has no code for a stake outside a range we set.${end}`;
  if (driver === 'JILI')
    return `${lead}the launch we send JILI carries a one-time token for the player, the game, the language, the address to come back to and the kind of device — no demo session, no bet range and no jackpot share.${end}`;
  return `${lead}no game here is launched with a demo session, a bet range or a jackpot share of ours.${end}`;
}

/**
 * The opening of the Providers tab's JILI notice when JILI's import last finished without failing.
 *
 * A run that finishes is not a run that refreshed anything: where JILI is switched off (staging, until
 * JILI issues its test details) the job finishes every hour having imported nothing. So the lead says
 * only what is true either way, that the import ran, and the run's own summary says what it did.
 * `since` is already in words ("2 hours ago"), so this stays free of the clock.
 */
export function jiliImportRanWords(summary: string | null, since: string): { lead: string; summary: string } {
  return { lead: `The JILI import last ran ${since}.`, summary: summary ?? 'The import completed.' };
}
